import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Printer } from "lucide-react";

import { Button } from "components/ui/Button";
import {
  BASE_STYLES,
  wrapForPrint,
} from "features/healthlink/utils/printDocument";

import styles from "./HealthlinkXsltFrame.module.css";

const HL7_NAMESPACE = "urn:hl7-org:v2xml";

/*
 * Where a Healthlink stylesheet's assets come from (KIN-2007).
 *
 * They used to come from test.healthlink.ie, hardcoded. That host serves them only to a client
 * presenting the Healthlink certificate, so in the browser they did not load: a certificate
 * prompt, unstyled documents, and an empty "(Age: years)" because displayage() lives in Utils.js.
 * In production it also meant every rendered document reaching for the *test* environment.
 *
 * The backend now inlines the CSS and JavaScript into the stylesheet itself, fetched with the
 * certificate it already holds. Images are not inlined — they are binary — so they are the one
 * kind of asset still fetched by the browser, and they are served from our own origin out of
 * public/healthlink/Images/.
 *
 * Absolute, not root-relative: the iframe is sandboxed without allow-same-origin, so it has an
 * opaque origin and "/healthlink/…" would have nothing to resolve against.
 */
const HEALTHLINK_ASSET_ORIGIN = `${window.location.origin}/healthlink`;

const isAbsoluteUrl = (url) => /^(https?:|data:|#|\/\/|mailto:)/i.test(url);

/*
 * Healthlink stylesheets reference assets with RELATIVE paths and inconsistent conventions
 * between message types — some foldered ("Images/x.gif", "Login/x.gif"), some flat ("x.gif").
 * A single <base> cannot satisfy all of them, so each relative src/href is rewritten to an
 * absolute URL, routing by its existing folder or, when flat, by file type.
 *
 * The .css and .js branches are dead for a Healthlink stylesheet now that the backend inlines
 * both. They are kept because the inlining is best-effort and behind a flag
 * (HEALTHLINK_XSL_INLINE_ASSETS_ENABLE): when it is off or a fetch fails, the reference survives
 * and still has to resolve somewhere, and our own origin is a better answer than the test host.
 */
const toAbsoluteHealthlinkUrl = (relative) => {
  const path = relative.replace(/^(\.\.\/)+|^\.?\//g, "");
  const lower = path.toLowerCase();

  if (/^(images|css|javascript)\//.test(lower)) return `${HEALTHLINK_ASSET_ORIGIN}/${path}`;
  // "Login/" logos live under /Images/.
  if (lower.startsWith("login/")) return `${HEALTHLINK_ASSET_ORIGIN}/Images/${path}`;
  if (lower.endsWith(".css")) return `${HEALTHLINK_ASSET_ORIGIN}/CSS/${path}`;
  if (lower.endsWith(".js")) return `${HEALTHLINK_ASSET_ORIGIN}/Javascript/${path}`;

  return `${HEALTHLINK_ASSET_ORIGIN}/Images/${path}`;
};

// Rewrite every relative asset URL in the transformed output to an absolute
// Healthlink URL so css/js/images all resolve correctly, independent of <base>.
const rewriteHealthlinkAssetUrls = (root) => {
  root.querySelectorAll("[src], [href]").forEach((element) => {
    ["src", "href"].forEach((attribute) => {
      const value = element.getAttribute(attribute);

      if (!value || isAbsoluteUrl(value)) return;

      element.setAttribute(attribute, toAbsoluteHealthlinkUrl(value));
    });
  });
};

/**
 * Healthlink stylesheets match elements in the HL7 v2xml namespace, but the
 * raw XML stored on the BE often lacks the xmlns declaration. Without it no
 * XSL template matches and the transform falls back to dumping plain text.
 * Inject the namespace into the root element when the stylesheet expects it.
 */
const alignHl7Namespace = (parser, xmlString, xslString) => {
  if (!xslString.includes(HL7_NAMESPACE)) return xmlString;

  const probe = parser.parseFromString(xmlString, "application/xml");
  const root = probe.documentElement;

  if (!root || root.namespaceURI) return xmlString;

  return xmlString.replace(
    new RegExp(`<${root.nodeName}([\\s>])`),
    `<${root.nodeName} xmlns="${HL7_NAMESPACE}"$1`
  );
};

/**
 * Transforms the XML with the XSLT and returns the resulting HTML string.
 * The fragment is assembled on a detached element so any scripts inside the
 * stylesheet output do NOT execute in the app context — the HTML is rendered
 * inside a sandboxed iframe instead (Healthlink stylesheets may contain
 * document.write calls that would otherwise wipe the whole page).
 */
const transformXmlWithXslt = (xmlString, xslString) => {
  const parser = new DOMParser();

  const alignedXml = alignHl7Namespace(parser, xmlString, xslString);

  const xmlDoc = parser.parseFromString(alignedXml, "application/xml");
  const xslDoc = parser.parseFromString(xslString, "application/xml");

  const processor = new XSLTProcessor();
  processor.importStylesheet(xslDoc);

  const resultFragment = processor.transformToFragment(xmlDoc, document);

  const detachedWrapper = document.createElement("div");
  detachedWrapper.appendChild(resultFragment);

  rewriteHealthlinkAssetUrls(detachedWrapper);

  return detachedWrapper.innerHTML;
};

// Toggle for OUR custom restyling of the Healthlink XSL output. When false the
// preview renders with the stylesheet's OWN CSS only (no Nunito, no overrides);
// flip to true to re-enable our styling. Kept in code on purpose so it's a
// one-line switch rather than a delete.
const APPLY_CUSTOM_STYLES = false;

// Structural-only reset — always applied so the iframe can measure its height
// and print correctly, regardless of the toggle above.
// Our custom restyling (Nunito + class-based overrides of the Healthlink XSL
// output). Healthlink stylesheets consistently use these class names
// (.heading, .blue, .red, hr.orange, *Specialised) across message types, so the
// rules stay structure-agnostic.
const CUSTOM_STYLES = `
      body { color: #1f2937; font-size: 14px; line-height: 1.5; }
      *, body, td, th, p, div, span { font-family: "Nunito", sans-serif !important; }
      table { width: 100%; border-collapse: collapse; }
      td, th { padding: 4px 8px; vertical-align: top; }
      th { font-weight: 700; }
      th.heading { font-size: 20px; line-height: 28px; font-weight: 700; padding: 12px 8px; color: #111827; }
      td.heading, .heading { font-weight: 700; color: #111827; }
      .blue { font-size: 15px; font-weight: 700; color: #111827; }
      td.general, .general { font-size: 15px; font-weight: 700; color: #111827; }
      th.generalrequestnologo, th.generalresponsenologo { font-size: 20px; line-height: 28px; font-weight: 700; color: #111827; }
      hr, hr.orange { border: none; border-top: 1px solid #e5e7eb; margin: 8px 0; }
      .red { font-weight: 700; }
      img { max-width: 100%; height: auto; }
      div.DivContainerSpecialised { position: relative; text-align: center; height: 100px; }
      table.TableContainerSpecialised { position: absolute; width: 100%; height: 100%; top: 0; left: 0; }
      table.TableContainerSpecialised td { vertical-align: middle; }
      span.specialised { color: #ffffff; font-size: 24px; font-weight: 700; }
      span.specialisedResponse { color: #ffffff; font-size: 21px; font-weight: 700; }`;

const CUSTOM_FONT_LINKS = `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap" rel="stylesheet">`;

const escapeHtml = (value) =>
  String(value).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

/*
 * Healthlink's own stylesheets log their working out — every table row they walk, every empty
 * cell they skip, a running total of rows. One lab order prints dozens of lines, which buries
 * whatever we were actually looking for in the console.
 *
 * It runs before the document body, because that is where their scripts are. Warnings and errors
 * are left alone: a document that genuinely fails to render should still say so.
 */
const SILENCE_DOCUMENT_LOGGING = `
      (function () {
        var quiet = function () {};
        if (window.console) {
          console.log = quiet;
          console.info = quiet;
          console.debug = quiet;
        }
      })();
`;

const buildIframeDocument = (bodyHtml, frameId, documentTitle) => `<!DOCTYPE html>
<html>
  <head>
    <title>${escapeHtml(documentTitle || DEFAULT_PRINT_TITLE)}</title>
    <base href="${HEALTHLINK_ASSET_ORIGIN}/Images/">${APPLY_CUSTOM_STYLES ? CUSTOM_FONT_LINKS : ""}
    <style>${BASE_STYLES}${APPLY_CUSTOM_STYLES ? CUSTOM_STYLES : ""}
    </style>
    <script>${SILENCE_DOCUMENT_LOGGING}</script>
  </head>
  <body>${bodyHtml}
    <script>
      (function () {
        var post = function () {
          // Measure the body, not documentElement — the latter is never
          // smaller than the iframe viewport, which ratchets the height up.
          parent.postMessage(
            {
              type: "healthlinkXsltFrameHeight",
              frameId: "${frameId}",
              height: document.body.scrollHeight,
            },
            "*"
          );
        };
        window.addEventListener("load", post);
        new ResizeObserver(post).observe(document.body);
      })();
    </script>
  </body>
</html>`;

// The print window is opened with window.open(""), so its document starts as about:blank. A document
// written into it with no <title> keeps that name — and the browser uses document.title for the print
// header, the tab, AND the filename it offers on "Save as PDF". One title fixes all three.
const DEFAULT_PRINT_TITLE = "Healthlink message";

// OBR.4 is the message's own name for itself, and every inbound type carries one: "MATERNAL DISCHARGE
// SUMMARY", "XR CHEST", "Biochemistry Tests", "Urinary Protein to Creatinine Ratio". Deriving the title
// here rather than asking each caller for one means every view that renders a Healthlink message gets a
// real title, including the ones nobody remembers to update.
const OBR4_NAME = /<OBR\.4>\s*(?:<CE\.1>[^<]*<\/CE\.1>\s*)?<CE\.2>([^<]*)<\/CE\.2>/;
const OBR7_DATE = /<OBR\.7>\s*<TS\.1>(\d{8})/;
// Not every message has an OBR at all: a REF_I12 discharge summary carries its content in NTE segments,
// with no observations to take a name or a date from. MSH.7 is the message timestamp and is always there.
const MSH7_DATE = /<MSH\.7>\s*<TS\.1>(\d{8})/;

// The message type as the API reports it — an enum name like DISCHARGE_SUMMARY. Prettified rather than
// looked up, so there is no table here to drift out of step with the backend's ~60 message types.
const prettifyKind = (kind) =>
  typeof kind === "string" && /^[A-Z0-9_]+$/.test(kind)
    ? kind
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ")
    : null;

// MN-CMS sends its document names in capitals where the spec writes them in title case, and a shouted
// heading reads badly on a printout. Only applied when the WHOLE string is upper case, so
// "BowelScreen result normal" is left exactly as the sender wrote it.
//
// Words of three letters or fewer keep their capitals, because in this vocabulary they are initialisms
// rather than words: "XR CHEST" should read "XR Chest", not "Xr Chest".
const softenAllCaps = (value) => {
  if (value !== value.toUpperCase() || !/[A-Z]{4}/.test(value)) return value;
  return value.replace(/[A-Za-z]+/g, (word) =>
    word.length <= 3 ? word : word[0] + word.slice(1).toLowerCase()
  );
};

const deriveTitleFromMessage = (xml, documentKind) => {
  if (!xml) return null;
  // OBR.4 is the message's own name for itself and is the most specific thing available. When there is
  // no OBR — REF_I12 discharge summaries and referral responses put their content in NTE — fall back to
  // what kind of message the API says it is.
  const name =
    softenAllCaps(OBR4_NAME.exec(xml)?.[1]?.trim() || "") || prettifyKind(documentKind);
  if (!name) return null;
  const raw = (OBR7_DATE.exec(xml) || MSH7_DATE.exec(xml))?.[1];
  const date = raw ? `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)}` : null;
  return [name, date].filter(Boolean).join(" - ");
};

export const HealthlinkXsltFrame = ({
  xml,
  xsl,
  withPrint = true,
  // Overrides the title derived from the message itself. Only worth passing when the caller knows
  // something the message does not say.
  documentTitle,
  // The message type as the API reports it (item.resultType). Used to name a message that has no OBR
  // segment to name itself — a REF_I12 discharge summary, for instance.
  documentKind,
  // Where the Print button floats. Defaults to the top-right corner, which is free in
  // every stylesheet but Healthlink's lab-order print — that one puts the barcode there.
}) => {
  const frameId = useId();
  const [renderedHtml, setRenderedHtml] = useState(null);
  const [renderError, setRenderError] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(null);
  // Looked up after render rather than at mount: the row that offers the slot belongs to a
  // sibling component, which is not guaranteed to be on the page before the document is.
  const [toolbarSlot, setToolbarSlot] = useState(null);
  const printTitle =
    documentTitle || deriveTitleFromMessage(xml, documentKind) || DEFAULT_PRINT_TITLE;

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data?.type === "healthlinkXsltFrameHeight" &&
        event.data.frameId === frameId &&
        event.data.height
      ) {
        setIframeHeight(event.data.height);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [frameId]);

  useEffect(() => {
    setRenderError(false);
    setRenderedHtml(null);

    if (!xml || !xsl) return;

    try {
      setRenderedHtml(transformXmlWithXslt(xml, xsl));
    } catch (error) {
      console.error("Failed to render Healthlink XML with XSLT:", error);
      setRenderError(true);
    }
  }, [xml, xsl]);

  // Keyed on the document, which is what the button is for. The row offering the slot is a
  // sibling in the same panel, so it is already mounted by the time a document has rendered.
  useEffect(() => {
    setToolbarSlot(document.querySelector("[data-healthlink-toolbar-slot]"));
  }, [renderedHtml]);

  const handlePrint = () => {
    if (!renderedHtml) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(
      buildIframeDocument(wrapForPrint(renderedHtml), "print", printTitle)
    );
    printWindow.document.close();
    printWindow.addEventListener("load", () => {
      printWindow.focus();
      printWindow.print();
    });
  };

  if (renderError) {
    return (
      <p className={styles.stateMessage}>
        Unable to render the message preview.
      </p>
    );
  }

  if (!renderedHtml) return null;

  /*
   * Above the document, never over it (KIN-1979). Every caller renders a Healthlink document
   * through Healthlink's own stylesheet, and several of those put a barcode in the top-right
   * corner, so an overlaid button lands on it. Which documents carry one is not ours to know,
   * and an offset tuned to one barcode stopped clearing it at other window widths — twice.
   *
   * A screen that already has a row of actions above the document can claim the button instead,
   * by rendering an element with data-healthlink-toolbar-slot. That keeps Print beside the
   * action it belongs with rather than on a line of its own, without the print handler having to
   * be threaded through every preview component between here and that row.
   */
  const toolbar = withPrint ? (
    <Button variant="outline" type="button" onClick={handlePrint} endIcon={Printer}>
      Print
    </Button>
  ) : null;

  return (
    <div className={styles.wrapper}>
      {!!toolbar &&
        (toolbarSlot ? (
          createPortal(toolbar, toolbarSlot)
        ) : (
          <div className={styles.toolbar}>{toolbar}</div>
        ))}
      <iframe
        title="Healthlink message preview"
        srcDoc={buildIframeDocument(renderedHtml, frameId, printTitle)}
        sandbox="allow-scripts"
        className={styles.viewerFrame}
        style={iframeHeight ? { height: `${iframeHeight}px` } : undefined}
      />
    </div>
  );
};
